/**
 * Singleton Laravel Echo (Reverb / Pusher-protocol).
 *
 * Зачем нужен:
 *  - Подписка на private-каналы `chat.thread.{externalId}` (стрим ответа LLM)
 *    и `support.user.{userId}` (счётчик непрочитанных + новые сообщения).
 *  - Один WebSocket на всю SPA — переподписка между диалогами без реконнекта.
 *
 * Аутентификация:
 *  - authEndpoint = `${BASE_URL}/api/broadcasting/auth` — кастомный
 *    `ChatBroadcastingAuthController` на бэке (см. routes/api.php).
 *    Поверх дефолтного broadcasting/auth он подкладывает фиктивного гостя
 *    в `$request->user()` для неавторизованных подписок — иначе встроенный
 *    `PusherBroadcaster::auth()` отвечает 403 на любую попытку подписки
 *    private-канала без Bearer-токена, и для гостевого режима чата WS
 *    «есть в сети», но никаких событий не получает.
 *  - Заголовки строим динамически через `authorizer`: и Bearer-token, и
 *    X-Chat-Session-Token могут появиться/обновиться в localStorage уже
 *    ПОСЛЕ инициализации Echo (первый REST-ответ кладёт session_token,
 *    логин кладёт Bearer). Делать снимок заголовков один раз — значит
 *    отправлять устаревшие куки при подписках после логина/выдачи session.
 *
 * Endpoint:
 *  - По умолчанию подключаемся к тому же origin что и API (через nginx
 *    `location ^/app/`). Это убирает CORS и облегчает прод-настройку
 *    (один сертификат, один host).
 */
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

if (typeof window !== 'undefined') {
    window.Pusher = Pusher
}

const BASE_URL = process.env.VUE_APP_URL_DEV || 'http://localhost:8080'
const APP_KEY = process.env.VUE_APP_REVERB_APP_KEY || 'wylvnpfqpynof1yhh1ek'

// Параметры WS-эндпоинта. Если хост не задан явно — берём из API_URL
// (тот же origin, тот же порт, что и REST). nginx сам разрулит /app/.
function resolveHost() {
    if (process.env.VUE_APP_REVERB_HOST) {
        return process.env.VUE_APP_REVERB_HOST
    }
    try {
        return new URL(BASE_URL).hostname
    } catch {
        return 'localhost'
    }
}

function resolvePort() {
    if (process.env.VUE_APP_REVERB_PORT) {
        return Number(process.env.VUE_APP_REVERB_PORT)
    }
    try {
        const parsed = new URL(BASE_URL)
        return parsed.port ? Number(parsed.port) : (parsed.protocol === 'https:' ? 443 : 80)
    } catch {
        return 8080
    }
}

function resolveScheme() {
    if (process.env.VUE_APP_REVERB_SCHEME) {
        return process.env.VUE_APP_REVERB_SCHEME
    }
    try {
        return new URL(BASE_URL).protocol === 'https:' ? 'https' : 'http'
    } catch {
        return 'http'
    }
}

const SESSION_KEY = 'chat_session_token'

let echoInstance = null

function buildAuthHeaders() {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : ''
    const sessionToken = typeof localStorage !== 'undefined'
        ? localStorage.getItem(SESSION_KEY)
        : ''
    const headers = { Accept: 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`
    if (sessionToken) headers['X-Chat-Session-Token'] = sessionToken
    return headers
}

// broadcasting/auth тоже проходит через middleware ChatGuestOrAuth, который
// при отсутствии валидного токена генерирует НОВЫЙ UUID и возвращает его в
// заголовке X-Chat-Session-Token. Если его не сохранить, токен для WS-канала
// разойдётся с тем, под которым REST создаёт тред (chat_threads.session_token),
// и колбэк chat.thread.* начнёт отвечать 403. Поэтому сохраняем выданный токен
// тем же ключом, что и chatApi.js.
function persistIssuedSession(response) {
    if (typeof localStorage === 'undefined') return
    const issued = response.headers.get('X-Chat-Session-Token')
    if (issued) localStorage.setItem(SESSION_KEY, issued)
}

export function getEcho() {
    if (echoInstance) {
        return echoInstance
    }

    const scheme = resolveScheme()
    const isHttps = scheme === 'https'
    const authUrl = `${BASE_URL}/api/broadcasting/auth`

    echoInstance = new Echo({
        broadcaster: 'reverb',
        key: APP_KEY,
        wsHost: resolveHost(),
        wsPort: resolvePort(),
        wssPort: resolvePort(),
        forceTLS: isHttps,
        enabledTransports: ['ws', 'wss'],
        // pusher-js нужны эти два, иначе он пытается ходить на pusher.com:
        cluster: 'mt1',
        disableStats: true,
        authEndpoint: authUrl,
        // Кастомный authorizer вместо статичных `auth.headers`: pusher-js
        // запросит его на каждую подписку private-канала и каждый раз
        // прочитает свежие токены из localStorage. Это критично для
        // гостевого режима — `X-Chat-Session-Token` появляется только
        // после первого REST-ответа (см. ChatGuestOrAuth middleware), а
        // Echo инициализируется при импорте `@/utils/echo` ещё ДО любых
        // REST-запросов. Со статическим snapshot'ом первая подписка ушла
        // бы без токена и получила 403.
        authorizer: (channel) => ({
            authorize: (socketId, callback) => {
                const body = new URLSearchParams({
                    socket_id: socketId,
                    channel_name: channel.name,
                })
                fetch(authUrl, {
                    method: 'POST',
                    headers: {
                        ...buildAuthHeaders(),
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: body.toString(),
                })
                    .then(async (response) => {
                        persistIssuedSession(response)
                        if (!response.ok) {
                            const text = await response.text().catch(() => '')
                            callback(new Error(`broadcasting/auth ${response.status}: ${text}`), null)
                            return
                        }
                        const data = await response.json().catch(() => ({}))
                        callback(null, data)
                    })
                    .catch((err) => callback(err, null))
            },
        }),
    })

    return echoInstance
}

/**
 * Полный teardown при logout — иначе следующий пользователь получит чужие
 * подписки. Вызывается из store/main.js при сбросе сессии.
 */
export function destroyEcho() {
    if (echoInstance) {
        try {
            echoInstance.disconnect()
        } catch {
            // noop
        }
        echoInstance = null
    }
}
